import type { v4, Manifest } from '@sap/ux-specification-types';
import { syncRule, validity } from '../../../common/decoration/decorators';
import { getCustomSectionPath, getCustomSubSectionPath } from '../../application';
import type { CustomSectionActionOP } from './ObjectPageSectionAction';

export class ObjectPageCustomSectionActions implements v4.ObjectPageCustomSectionActions {
    [key: string]: CustomSectionActionOP;
}

export class ObjectPageCustomSectionFragment implements v4.ObjectPageCustomSectionFragment {
    @syncRule({
        manifest: {
            path: (pageName: string[], manifest: Manifest, sectionId): string => {
                return getCustomSectionPath(pageName, manifest, sectionId, 'position');
            },
            key: 'anchor'
        }
    })
    @validity({
        since: '1.77.0'
    })
    relatedFacet: string;

    @syncRule({
        manifest: {
            path: (pageName: string[], manifest: Manifest, sectionId: string): string =>
                getCustomSectionPath(pageName, manifest, sectionId, 'position'),
            key: 'placement'
        }
    })
    @validity({
        since: '1.77.0'
    })
    relativePosition: v4.SectionPosition;

    @syncRule({
        manifest: {
            path: getCustomSectionPath
        }
    })
    @validity({
        since: '1.77.0'
    })
    title: string;

    @syncRule({
        manifest: {
            path: getCustomSectionPath,
            import: (manifestSection: v4.SapUi5RoutingTargetContentSection): string => {
                // During import, we need to consider both `name` and `template` to correctly fill our config and to be upgrade safe
                return manifestSection.name || manifestSection.template;
            },
            key: 'template' // key: 'name' deprecated since '1.86'
        }
    })
    @validity({
        since: '1.77.0'
    })
    fragmentName: string;

    @syncRule({
        manifest: {
            path: getCustomSectionPath,
            import: false,
            export: false
        },
        xml: (config: object): string => {
            return config['fragmentName'];
        }
    })
    @validity({
        since: '1.96.0'
    })
    controls: object;

    actions: ObjectPageCustomSectionActions;
}

export class ObjectPageCustomSubSectionFragment
    extends ObjectPageCustomSectionFragment
    implements v4.ObjectPageCustomSubSectionFragment
{
    @syncRule({
        manifest: {
            path: (...args): string => {
                const resolvedPath = getCustomSubSectionPath.apply({}, args);
                return `${resolvedPath}/position`;
            },
            key: 'anchor'
        }
    })
    @validity({
        since: '1.77.0'
    })
    declare relatedFacet: string;

    @syncRule({
        manifest: {
            path: (...args): string => {
                const resolvedPath = getCustomSubSectionPath.apply({}, args);
                return `${resolvedPath}/position`;
            },
            key: 'placement'
        }
    })
    @validity({
        since: '1.77.0'
    })
    declare relativePosition: v4.SectionPosition;

    @syncRule({
        manifest: {
            path: getCustomSubSectionPath
        }
    })
    @validity({
        since: '1.77.0'
    })
    declare title: string;

    @syncRule({
        manifest: {
            path: getCustomSubSectionPath,
            import: (manifestSection: v4.SapUi5RoutingTargetContentSection): string => {
                // During import, we need to consider both `name` and `template` to correctly fill our config and to be upgrade safe
                return manifestSection.name || manifestSection.template;
            },
            key: 'template' // key: 'name' deprecated since '1.86'
        }
    })
    @validity({
        since: '1.77.0'
    })
    declare fragmentName: string;

    @syncRule({
        manifest: {
            path: getCustomSubSectionPath,
            import: false,
            export: false
        },
        xml: (config: object): string => {
            return config['fragmentName'];
        }
    })
    @validity({
        since: '1.96.0'
    })
    declare controls: object;
}
